import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bookmark, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LoginModal } from './LoginModal';

const STORAGE_KEY = 'login_nudge_dismissed';
const DISMISS_DAYS = 1;

export function LoginNudge() {
  const { t } = useTranslation();
  const { user, loading } = useAuth() || {};
  const [visible, setVisible] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    if (loading || user) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const daysAgo = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (daysAgo < DISMISS_DAYS) return;
    }
    const timer = setTimeout(() => setVisible(true), 20000);
    return () => clearTimeout(timer);
  }, [user, loading]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  const goToLogin = () => {
    setVisible(false);
    setShowLoginModal(true);
  };

  if (!visible && !showLoginModal) return null;

  return (
    <>
      {visible && (
        <div data-testid="login-nudge"
          className="fixed bottom-6 right-6 z-50 max-w-sm w-full sm:w-auto animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 flex gap-3 items-start">
            <div className="flex gap-1.5 mt-0.5 shrink-0">
              <div className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Bookmark size={13} className="text-amber-500" />
              </div>
              <div className="w-7 h-7 rounded-full bg-blue-500/15 flex items-center justify-center">
                <Bell size={13} className="text-blue-500" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground leading-snug mb-0.5">{t('nudgeTitle')}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{t('nudgeDesc')}</p>
              <div className="flex gap-2 mt-2.5">
                <button data-testid="login-nudge-login-btn" onClick={goToLogin}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-foreground text-background hover:opacity-90 active:scale-95 transition-all">
                  {t('login')}
                </button>
                <button data-testid="login-nudge-dismiss-btn" onClick={dismiss}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors">
                  {t('nudgeDismiss')}
                </button>
              </div>
            </div>
            <button onClick={dismiss} className="shrink-0 text-muted-foreground hover:text-foreground transition-colors mt-0.5">
              <X size={14} />
            </button>
          </div>
        </div>
      )}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
    </>
  );
}
